import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
export function Location() {
  const {
    t
  } = useLanguage();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const lat = 40.9547;
      const lng = 0.8833;
      mapInstanceRef.current = L.map(mapRef.current).setView([lat, lng], 15);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      }).addTo(mapInstanceRef.current);
      L.marker([lat, lng]).addTo(mapInstanceRef.current).bindPopup('<b>Restaurant el Astillero</b><br>Urb. San Jorge S Casti, 152').openPopup();
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  const contactItems = [{
    icon: MapPinIcon,
    title: t.location.address,
    content: "Urb. San Jorge S Casti, 152\n43860 Sant Jordi d'Alfama\nTarragona"
  }, {
    icon: PhoneIcon,
    title: t.location.phone,
    content: '+34 655 14 81 65'
  }, {
    icon: MailIcon,
    title: t.location.email,
    content: 'info@elastillero.com'
  }, {
    icon: ClockIcon,
    title: t.location.hours,
    content: t.location.hoursText
  }];
  return <section id="contact" className="py-28 lg:py-36 bg-cream bg-sand-texture relative" data-id="element-144">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-145">
        <div className="text-center mb-18" data-id="element-146">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-6" data-id="element-147">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/50" data-id="element-148"></div>
            <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-149">
              {t.location.subtitle}
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/50" data-id="element-150"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-navy" data-id="element-151">
            {t.location.title}
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 25
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="flex flex-col lg:flex-row bg-white rounded-sm overflow-hidden shadow-elegant" data-id="element-152">
          {/* Contact Info */}
          <div className="w-full lg:w-5/12 p-10 lg:p-14 flex flex-col justify-center space-y-10 bg-navy text-white" data-id="element-153">
            {contactItems.map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1,
            duration: 0.6
          }} className="flex items-start space-x-4" data-id="element-154">
                <div className="w-10 h-10 bg-sand/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-155">
                  <item.icon className="w-4 h-4 text-sand" data-id="element-156" />
                </div>
                <div data-id="element-157">
                  <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-sand mb-2" data-id="element-158">
                    {item.title}
                  </h4>
                  <p className="text-white/60 font-light text-sm whitespace-pre-line leading-relaxed" data-id="element-159">
                    {item.content}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Map */}
          <div className="w-full lg:w-7/12 h-[400px] lg:h-auto min-h-[500px] relative z-0" data-id="element-160">
            <div ref={mapRef} className="absolute inset-0 w-full h-full" data-id="element-161" />
          </div>
        </motion.div>
      </div>
    </section>;
}